import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Select, message, Button, Radio, Space } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import { useDispatch, useSelector } from 'react-redux';
import { PlusOutlined, DeleteOutlined, UserOutlined, UserAddOutlined } from '@ant-design/icons';

import { CompetitionWinner } from '../../../core/competition/types';
import { Teacher } from '../../../core/school/types';
import { gradeOptions, placeLevelOptions, subjectOptions } from '../../../core/utils/helpers';
import { getSchools } from '../../../core/school/schoolApi';
import { AppDispatch, CombinedStates } from '../../../core/types';

const { Option } = Select;
const { TextArea } = Input;

interface CreateCompetitionWinnerModalProps {
    visible: boolean;
    onCancel: () => void;
    onSubmit: (values: Partial<CompetitionWinner>) => void;
    loading?: boolean;
    winner?: CompetitionWinner | null;
    competitionId: number;
}

interface TeacherInput {
    id: string | number;
    first_name: string;
    last_name: string;
    subject: string;
    isExistingTeacher?: boolean;
    teacher_id?: number | null;
}

const CreateCompetitionWinnerModal: React.FC<CreateCompetitionWinnerModalProps> = ({
    visible,
    onCancel,
    onSubmit,
    loading = false,
    winner = null,
    competitionId,
}) => {
    const [form] = useForm();
    const dispatch = useDispatch<AppDispatch>();
    const [teachers, setTeachers] = useState<TeacherInput[]>([]);
    const [selectedSchoolId, setSelectedSchoolId] = useState<number | null>(null);

    const { schools, isFetching: isFetchingSchools } = useSelector((state: CombinedStates) => state.school);

    // Get selected school's teachers
    const selectedSchool = schools.find(school => school.id === selectedSchoolId);
    const schoolTeachers = selectedSchool?.teachers || [];

    useEffect(() => {
        if (visible) {
            dispatch(getSchools({}));
        }
    }, [visible, dispatch]);

    useEffect(() => {
        if (visible && winner) {
            form.setFieldsValue({
                participant_name: winner.participant_name,
                school_id: winner.school_id,
                grade: winner.grade,
                place_level: winner.place_level,
                notes: winner.notes,
            });

            setSelectedSchoolId(winner.school_id);
        } else if (visible) {
            form.resetFields();
            setTeachers([]);
            setSelectedSchoolId(null);
        }
    }, [visible, winner, form]);

    // Handle teacher loading when school data is available
    useEffect(() => {
        if (visible && winner && selectedSchool && winner.teachers) {
            const teacherInputs = winner.teachers.map(teacher => {
                // Check if this teacher exists in the school's teachers list
                const existingTeacher = selectedSchool.teachers?.find(schoolTeacher => (schoolTeacher.first_name === teacher.first_name
                    && schoolTeacher.last_name === teacher.last_name));

                return {
                    id: Math.random().toString(),
                    first_name: teacher.first_name,
                    last_name: teacher.last_name,
                    subject: teacher.pivot?.subject || '',
                    isExistingTeacher: !!existingTeacher,
                    teacher_id: existingTeacher?.id || null,
                };
            });
            setTeachers(teacherInputs);
        }
    }, [visible, winner, selectedSchool]);

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();

            // Validate teachers
            if (teachers.length === 0) {
                message.error('Prašome pridėti bent vieną mokytoją');
                return;
            }

            const teachersData = teachers.map((teacher: TeacherInput) => {
                const teacherData: any = {
                    subject: teacher.subject,
                };

                if (teacher.isExistingTeacher) {
                    if (!teacher.teacher_id) {
                        throw new Error('Prašome pasirinkti mokytoją iš sąrašo');
                    }
                    // Existing teacher - send teacher_id
                    teacherData.teacher_id = teacher.teacher_id;
                } else {
                    // New teacher - send first_name and last_name
                    if (!teacher.first_name.trim() || !teacher.last_name.trim()) {
                        throw new Error('Prašome užpildyti mokytojo vardą ir pavardę');
                    }
                    teacherData.first_name = teacher.first_name;
                    teacherData.last_name = teacher.last_name;
                }

                if (!teacher.subject) {
                    throw new Error('Prašome pasirinkti dalyką');
                }

                return teacherData;
            });

            const formattedValues: Partial<CompetitionWinner> = {
                ...values,
                competition_id: competitionId,
                teachers: teachersData,
            };

            if (winner?.id) {
                formattedValues.id = winner.id;
            }

            onSubmit(formattedValues);
        } catch (error) {
            if (error instanceof Error) {
                message.error(error.message);
            } else {
                message.error('Prašome užpildyti visus privalomus laukus');
            }
        }
    };

    const handleCancel = () => {
        form.resetFields();
        setTeachers([]);
        setSelectedSchoolId(null);
        onCancel();
    };

    const addTeacher = () => {
        const newTeacher: TeacherInput = {
            id: Math.random().toString(),
            first_name: '',
            last_name: '',
            subject: '',
            isExistingTeacher: false,
        };
        setTeachers([...teachers, newTeacher]);
    };

    const removeTeacher = (id: string | number) => {
        setTeachers(teachers.filter(teacher => teacher.id !== id));
    };

    const updateTeacher = (id: string | number, field: keyof TeacherInput, value: any) => {
        setTeachers(teachers.map(teacher => {
            if (teacher.id === id) {
                const updatedTeacher = { ...teacher, [field]: value };

                // If switching to existing teacher, clear the name fields
                if (field === 'isExistingTeacher' && value === true) {
                    updatedTeacher.first_name = '';
                    updatedTeacher.last_name = '';
                    updatedTeacher.teacher_id = null;
                }

                // If switching to new teacher, clear the teacher_id
                if (field === 'isExistingTeacher' && value === false) {
                    updatedTeacher.teacher_id = null;
                }

                return updatedTeacher;
            }
            return teacher;
        }));
    };

    const handleSchoolChange = (schoolId: number) => {
        setSelectedSchoolId(schoolId);
        // Clear teachers when school changes
        setTeachers([]);
    };

    const handleExistingTeacherSelect = (teacherId: number, teacherInputId: string | number) => {
        console.log('Selecting teacher:', teacherId, 'for input:', teacherInputId);
        const selectedTeacher = schoolTeachers.find(teacher => teacher.id === teacherId);
        console.log('Found teacher:', selectedTeacher);
        if (selectedTeacher) {
            setTeachers(prevTeachers => {
                const updatedTeachers = prevTeachers.map(teacher => (teacher.id === teacherInputId
                    ? {
                        ...teacher,
                        teacher_id: teacherId,
                        first_name: selectedTeacher.first_name,
                        last_name: selectedTeacher.last_name,
                    }
                    : teacher));
                console.log('Updated teachers:', updatedTeachers);
                return updatedTeachers;
            });
        }
    };

    return (
        <Modal
            title={winner?.id ? 'Redaguoti nugalėtoją' : 'Sukurti naują nugalėtoją'}
            open={visible}
            onOk={handleSubmit}
            onCancel={handleCancel}
            confirmLoading={loading}
            width={700}
            destroyOnClose
            okText={winner?.id ? 'Atnaujinti' : 'Sukurti'}
            cancelText="Atšaukti">
            <Form
                form={form}
                layout="vertical"
                initialValues={{
                    grade: 9,
                    place_level: 'I',
                }}>
                <Form.Item
                    name="participant_name"
                    label="Nugalėtojo vardas"
                    rules={[{ required: true, message: 'Prašome įvesti dalyvio vardą' }]}>
                    <Input placeholder="Įveskite dalyvio vardą ir pavardę" />
                </Form.Item>

                <Form.Item
                    name="school_id"
                    label="Mokykla"
                    rules={[{ required: true, message: 'Prašome pasirinkti mokyklą' }]}>
                    <Select
                        placeholder="Pasirinkite mokyklą"
                        loading={isFetchingSchools}
                        showSearch
                        onChange={handleSchoolChange}
                        filterOption={(input, option) => (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())}>
                        {schools.map(school => (
                            <Option key={school.id} value={school.id}>
                                {school.name}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item
                    name="grade"
                    label="Klasė"
                    rules={[{ required: true, message: 'Prašome pasirinkti klasę' }]}>
                    <Select placeholder="Pasirinkite klasę">
                        {gradeOptions.map(grade => (
                            <Option key={grade.value} value={grade.value}>
                                {grade.label}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item
                    name="place_level"
                    label="Vieta"
                    rules={[{ required: true, message: 'Prašome pasirinkti vietą' }]}>
                    <Select placeholder="Pasirinkite vietą">
                        {placeLevelOptions.map(place => (
                            <Option key={place.value} value={place.value}>
                                {place.label}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item label="Mokytojai">
                    <div style={{ marginBottom: 8 }}>
                        <Button
                            type="dashed"
                            onClick={addTeacher}
                            icon={<PlusOutlined />}
                            style={{ width: '100%' }}>
                            Pridėti mokytoją
                        </Button>
                    </div>
                    {teachers.map((teacher, index) => (
                        <div key={teacher.id}
                            style={{
                                border: '1px solid #d9d9d9',
                                borderRadius: '6px',
                                padding: '12px',
                                marginBottom: '8px',
                                backgroundColor: '#fafafa',
                            }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                <span style={{ fontWeight: 'bold' }}>Mokytojas {index + 1}</span>
                                <Button
                                    type="text"
                                    danger
                                    icon={<DeleteOutlined />}
                                    onClick={() => removeTeacher(teacher.id)}
                                    size="small" />
                            </div>

                            <div style={{ marginBottom: '8px' }}>
                                <Radio.Group
                                    value={teacher.isExistingTeacher}
                                    onChange={(e) => updateTeacher(teacher.id, 'isExistingTeacher', e.target.value)}
                                    style={{ marginBottom: '8px' }}>
                                    <Space direction="vertical">
                                        <Radio value={false}>
                                            <Space>
                                                <UserAddOutlined />
                                                Sukurti naują mokytoją
                                            </Space>
                                        </Radio>
                                        <Radio value disabled={!selectedSchoolId || schoolTeachers.length === 0}>
                                            <Space>
                                                <UserOutlined />
                                                Pasirinkti iš esamų mokytojų
                                                {(!selectedSchoolId || schoolTeachers.length === 0)
                                                    && (
                                                        <span style={{ color: '#999', fontSize: '12px' }}>
                                                            (mokykla neturi mokytojų)
                                                        </span>
                                                    )}
                                            </Space>
                                        </Radio>
                                    </Space>
                                </Radio.Group>
                            </div>

                            {teacher.isExistingTeacher ? (
                                // Existing teacher selection
                                <div style={{ marginBottom: '8px' }}>
                                    <Select
                                        placeholder="Pasirinkite mokytoją *"
                                        value={teacher.teacher_id || undefined}
                                        onChange={(value) => value && handleExistingTeacherSelect(value, teacher.id)}
                                        style={{ width: '100%' }}
                                        showSearch
                                        status={teacher.isExistingTeacher && !teacher.teacher_id ? 'error' : ''}
                                        filterOption={(input, option) => String(option?.children || '').toLowerCase().includes(input.toLowerCase())}>
                                        {schoolTeachers.map(schoolTeacher => (
                                            <Option key={schoolTeacher.id} value={schoolTeacher.id}>
                                                {schoolTeacher.first_name} {schoolTeacher.last_name}
                                            </Option>
                                        ))}
                                    </Select>
                                    {teacher.teacher_id && (
                                        <div style={{
                                            marginTop: '4px',
                                            padding: '4px 8px',
                                            backgroundColor: '#e6f7ff',
                                            borderRadius: '4px',
                                            fontSize: '12px',
                                            color: '#fdab01',
                                        }}>
                                            Pasirinktas: {teacher.first_name} {teacher.last_name}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                // New teacher input fields
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                                    <Input
                                        placeholder="Vardas"
                                        value={teacher.first_name}
                                        onChange={(e) => updateTeacher(teacher.id, 'first_name', e.target.value)} />
                                    <Input
                                        placeholder="Pavardė"
                                        value={teacher.last_name}
                                        onChange={(e) => updateTeacher(teacher.id, 'last_name', e.target.value)} />
                                </div>
                            )}

                            <Select
                                placeholder="Dalykas"
                                value={teacher.subject || undefined}
                                onChange={(value) => updateTeacher(teacher.id, 'subject', value)}
                                style={{ width: '100%' }}
                                status={!teacher.subject ? 'error' : ''}>
                                {subjectOptions.map(subject => (
                                    <Option key={subject.value} value={subject.value}>
                                        {subject.label}
                                    </Option>
                                ))}
                            </Select>
                        </div>
                    ))}
                </Form.Item>

                <Form.Item
                    name="notes"
                    label="Pastabos">
                    <TextArea
                        rows={3}
                        placeholder="Papildoma informacija apie nugalėtoją"
                        maxLength={500}
                        showCount />
                </Form.Item>
            </Form>
        </Modal>
    );
};

CreateCompetitionWinnerModal.defaultProps = {
    loading: false,
    winner: null,
};

export default CreateCompetitionWinnerModal;
